// BUILD-046 correction cycle 1bb88b3e (stage B1, items N5 and N6): Done with a note.
//
// ORDINARY DONE IS UNCHANGED (doneModel.ts): one click, one request, no body, no note, no date. "Done with a note" is a second, nearby choice that opens a
// compact OPTIONAL note and an explicit "Complete task". It is offered exactly where Done is offered (the current assignee of an ordinary open task); a
// source-governed task keeps its existing behavior - no Done, no Done with a note, it completes through its source.
//
// The note is an attributed, dated ACTIVITY entry the server records in the same step as the completion (POST /work/{id}/complete with
// { note: { text, activityDate? } }). It never replaces the task description or another person's note, and completing never sets a next action. The activity
// date defaults to today in Phoenix, may be earlier, and may not be later (the client checks too; the SERVER decides).
//
// TRUTH RULES (the reason this module exists):
//   - A note was sent => it is saved ONLY when the answer says `noteSaved === true`. Anything else - a 200 without the flag (an older server), a
//     `noteSaved: false`, an `alreadyCompleted` answer, a failure of any kind - means NOT SAVED, and the text the person typed is KEPT.
//   - The outcome says what happened to the TASK and to the NOTE separately: completed + note saved; completed without a note (nothing was typed);
//     completed but the note was not saved; already completed and the note was not saved; not completed (the request failed or was refused); not
//     confirmed (no answer: either may have happened); changed meanwhile (reload).
//   - A failure leaves the task open, the draft in place and the choice to try again. The words never claim "saved" for anything that is not.
//   - Empty text means no note: "Complete task" is then an ordinary Done (no body), reported as "completed, no note added".
//
// Pure module: no React, no I/O, no clock (the caller supplies today's Phoenix date). Runtime imports: the Done model and the date helpers. Erasable TypeScript.

import { isFutureDate, isIsoDate } from './dateLabels';
import { activateDone, doneControl, doneSettled, doneSucceeded } from './doneModel';
import type { DoneListView, DoneRow, DoneState, DoneViewer } from './doneModel';

const OPEN_STATUSES: readonly string[] = ['active', 'in_progress', 'waiting', 'blocked'];

/** Shown instead of Done and Done with a note on a source-governed task the Caller owns: the existing "completes through its source" behavior. */
export const COMPLETES_THROUGH_SOURCE_TEXT = 'This task completes through its source.';
export const COMPLETE_TASK_LABEL = 'Complete task';
export const COMPLETING_LABEL = 'Completing…';
/** The plain outcome words. "Saved" is said ONLY for an answer that carried `noteSaved === true`. */
export const SAVED_TEXT = 'Completed. Note saved.';
export const NO_NOTE_TEXT = 'Completed. No note added.';
export const NOT_COMPLETED_KEPT_TEXT = 'Not completed. Your note is kept. Try again.';
export const NOT_COMPLETED_TEXT = 'Not completed. Try again.';
/** The control beside Done, its tooltip and the panel's labels. */
export const DONE_WITH_NOTE_LABEL = 'Done with note';
export const DONE_WITH_NOTE_TOOLTIP = 'Complete this task and record a note';
export const NOTE_FIELD_LABEL = 'Completion note (optional)';

export interface WorkNote {
  text: string;
  activityDate?: string;
}

export type NoteOutcomeKind =
  | 'completed-note-saved'
  | 'completed-no-note'
  | 'completed-note-unsaved'
  | 'already-completed-note-unsaved'
  | 'not-completed'
  | 'unconfirmed'
  | 'changed';

export interface NoteOutcome {
  kind: NoteOutcomeKind;
  tone: 'success' | 'warning' | 'error';
  headline: string;
  detail: string | null;
}

export interface NoteFieldErrors {
  text: string | null;
  activityDate: string | null;
}

export interface DoneNoteItem {
  /** The compact note input is open. */
  open: boolean;
  text: string;
  /** `YYYY-MM-DD`; today (Phoenix) when opened. Sent only when the person changed it. */
  activityDate: string;
  dateTouched: boolean;
  saving: boolean;
  fieldErrors: NoteFieldErrors;
  outcome: NoteOutcome | null;
  /** The task is completed (by this request, or already): completing again is pointless, and the draft is kept only so the text is not lost. */
  taskCompleted: boolean;
  /** The list should be read again (the task changed meanwhile, or the result could not be confirmed). */
  needsReload: boolean;
}

export interface DoneNoteState {
  readonly items: Readonly<Record<string, DoneNoteItem>>;
}

const NO_ERRORS: NoteFieldErrors = Object.freeze({ text: null, activityDate: null });

const IDLE: DoneNoteItem = Object.freeze({ open: false, text: '', activityDate: '', dateTouched: false, saving: false, fieldErrors: NO_ERRORS, outcome: null, taskCompleted: false, needsReload: false }) as DoneNoteItem;

export function initialNoteState(): DoneNoteState {
  return { items: {} };
}

export function itemNote(state: DoneNoteState, id: string): DoneNoteItem {
  return state.items[id] ?? IDLE;
}

function put(state: DoneNoteState, id: string, next: DoneNoteItem): DoneNoteState {
  return { items: { ...state.items, [id]: next } };
}

/** A Caller change: nothing the previous Caller typed or was told survives. */
export function resetNoteState(): DoneNoteState {
  return initialNoteState();
}

// ───────────────────────── who is offered it ─────────────────────────

export interface DoneNoteOffer {
  /** "Done with note" is offered beside Done. */
  offered: boolean;
  /** For an open source-governed task the viewer owns: the existing explanation, with neither Done nor Done with a note. */
  governedText: string | null;
}

/** Offered exactly where Done is offered (doneModel.doneControl). A source-governed task shows the explanation instead. */
export function doneNoteOffer(row: DoneRow, viewer: DoneViewer | null, view: DoneListView): DoneNoteOffer {
  if (doneControl(row, viewer, view) === 'done') return { offered: true, governedText: null };
  const owned = viewer !== null && typeof row.ownerEmployeeId === 'string' && row.ownerEmployeeId !== '' && row.ownerEmployeeId === viewer.employeeId;
  const open = row.status === 'active' || row.status === 'in_progress' || row.status === 'waiting' || row.status === 'blocked';
  if (owned && open && row.sourceGoverned === true && row.redacted !== true && row.lifecycle !== 'cancelled') return { offered: false, governedText: COMPLETES_THROUGH_SOURCE_TEXT };
  return { offered: false, governedText: null };
}

// ───────────────────────── editing the draft ─────────────────────────

/** Opens the compact note with the activity date defaulted to today (Phoenix). Opening again keeps what is typed. */
export function openNote(state: DoneNoteState, id: string, today: string): DoneNoteState {
  const current = itemNote(state, id);
  if (current.open || current.saving) return state;
  return put(state, id, { ...current, open: true, activityDate: current.dateTouched ? current.activityDate : today, fieldErrors: NO_ERRORS, outcome: current.taskCompleted ? current.outcome : null });
}

export function setNoteText(state: DoneNoteState, id: string, text: string): DoneNoteState {
  const current = itemNote(state, id);
  if (!current.open || current.saving || typeof text !== 'string') return state;
  return put(state, id, { ...current, text, fieldErrors: { ...current.fieldErrors, text: null } });
}

export function setNoteDate(state: DoneNoteState, id: string, activityDate: string): DoneNoteState {
  const current = itemNote(state, id);
  if (!current.open || current.saving || typeof activityDate !== 'string') return state;
  return put(state, id, { ...current, activityDate, dateTouched: true, fieldErrors: { ...current.fieldErrors, activityDate: null } });
}

/**
 * Closes the panel WITHOUT discarding (the Escape key and the "Done with note" button used as a toggle): what was typed, the date and any message are kept,
 * and opening it again shows them. An entry with nothing in it is removed so no empty state lingers. Not while a request runs.
 */
export function closeNote(state: DoneNoteState, id: string): DoneNoteState {
  const current = itemNote(state, id);
  if (current.saving || !current.open) return state;
  if (current.text === '' && !current.dateTouched && current.outcome === null) return discardNote(state, id);
  return put(state, id, { ...current, open: false, fieldErrors: NO_ERRORS });
}

/** Cancel: the draft is discarded on purpose. Not while a request runs. */
export function discardNote(state: DoneNoteState, id: string): DoneNoteState {
  const current = itemNote(state, id);
  if (current.saving) return state;
  if (state.items[id] === undefined) return state;
  const rest = { ...state.items };
  delete rest[id];
  return { items: rest };
}

/** Dismisses the outcome message only. A kept draft stays. */
export function dismissNoteOutcome(state: DoneNoteState, id: string): DoneNoteState {
  const current = itemNote(state, id);
  if (current.outcome === null) return state;
  return put(state, id, { ...current, outcome: null });
}

// ───────────────────────── validation ─────────────────────────

export const TEXT_TOO_LONG = 'The note is too long.';
export const DATE_INVALID = 'Enter the date as YYYY-MM-DD.';
export const DATE_IN_FUTURE = 'A note records work already done. Choose today or an earlier date.';

/** The note to send, or null when nothing was typed (that is an ordinary Done). The date is sent only when the person changed it. */
export function noteToSend(item: DoneNoteItem): WorkNote | null {
  const text = item.text.trim();
  if (text === '') return null;
  const date = item.dateTouched && item.activityDate !== '' ? item.activityDate : undefined;
  return date === undefined ? { text } : { text, activityDate: date };
}

/** Checks what can be checked here; the server decides. Only a date the person entered is checked (the default is today). */
export function validateNote(item: DoneNoteItem, today: string): NoteFieldErrors {
  let activityDate: string | null = null;
  if (item.text.trim() !== '' && item.dateTouched && item.activityDate !== '') {
    if (!isIsoDate(item.activityDate)) activityDate = DATE_INVALID;
    else if (isFutureDate(item.activityDate, today)) activityDate = DATE_IN_FUTURE;
  }
  return { text: null, activityDate };
}

// ───────────────────────── the click ─────────────────────────

/**
 * The "Complete task" click. `send` is non-null exactly when the caller must now send the request: `{ note: null }` is an ordinary Done (nothing typed),
 * `{ note }` a Done with a note. The Done state goes to `saving` (the row's Done is disabled) at the same time; a second click while it runs, a task that
 * is not open, and a note that fails the checks send nothing (the checks name the field).
 */
export function attemptNoteComplete(
  note: DoneNoteState,
  done: DoneState,
  id: string,
  currentStatus: string,
  today: string
): { note: DoneNoteState; done: DoneState; send: { note: WorkNote | null } | null } {
  const current = itemNote(note, id);
  // A task that is not open sends nothing (activateDone); a stale `taskCompleted` from before an Undo does not block a task that is open again.
  if (current.saving) return { note, done, send: null };
  const errors = validateNote(current, today);
  if (errors.text !== null || errors.activityDate !== null) {
    return { note: put(note, id, { ...current, open: true, fieldErrors: errors, outcome: null }), done, send: null };
  }
  const activation = activateDone(done, id, currentStatus);
  if (!activation.send) return { note, done, send: null };
  return {
    note: put(note, id, { ...current, open: true, saving: true, fieldErrors: NO_ERRORS, outcome: null, needsReload: false }),
    done: activation.state,
    send: { note: noteToSend(current) },
  };
}

// ───────────────────────── the answers ─────────────────────────

export interface CompleteAnswer {
  alreadyCompleted?: unknown;
  noteSaved?: unknown;
  noteReason?: unknown;
}

export interface Classified {
  kind: 'completed-note-saved' | 'completed-no-note' | 'completed-note-unsaved' | 'already-completed-note-unsaved';
  /** This request completed the task (so Undo may be offered). */
  completedNow: boolean;
}

/** What an ACCEPTED answer means. The note is saved only when the answer says `noteSaved === true`. */
export function classifyAnswer(answer: CompleteAnswer | null | undefined, noteSent: boolean): Classified {
  const already = answer !== null && answer !== undefined && answer.alreadyCompleted === true;
  if (!noteSent) return { kind: 'completed-no-note', completedNow: !already };
  if (answer !== null && answer !== undefined && answer.noteSaved === true) return { kind: 'completed-note-saved', completedNow: !already };
  if (already) return { kind: 'already-completed-note-unsaved', completedNow: false };
  return { kind: 'completed-note-unsaved', completedNow: true };
}

function outcomeFor(classified: Classified, answer: CompleteAnswer | null | undefined): NoteOutcome {
  switch (classified.kind) {
    case 'completed-note-saved':
      return { kind: classified.kind, tone: 'success', headline: SAVED_TEXT, detail: null };
    case 'completed-no-note':
      return { kind: classified.kind, tone: 'success', headline: NO_NOTE_TEXT, detail: null };
    case 'completed-note-unsaved':
      return { kind: classified.kind, tone: 'warning', headline: 'Completed. Note not saved.', detail: 'Your text is kept here so it is not lost.' };
    case 'already-completed-note-unsaved': {
      const reason = answer !== null && answer !== undefined && answer.noteReason === 'ALREADY_COMPLETED' ? 'Note not saved. This task was already completed.' : 'Note not saved.';
      return { kind: classified.kind, tone: 'warning', headline: reason, detail: 'Your text is kept here so it is not lost.' };
    }
  }
}

/** The request was accepted. Applies only to a request in progress. */
export function noteCompleted(note: DoneNoteState, done: DoneState, id: string, answer: CompleteAnswer | null | undefined, noteSent: boolean): { note: DoneNoteState; done: DoneState } {
  const current = itemNote(note, id);
  if (!current.saving) return { note, done };
  const classified = classifyAnswer(answer, noteSent);
  const outcome = outcomeFor(classified, answer);
  const nextDone = classified.completedNow ? doneSucceeded(done, id) : doneSettled(done, id);
  const finished = classified.kind === 'completed-note-saved' || classified.kind === 'completed-no-note';
  if (finished) {
    // Nothing is left to keep: the note is saved, or nothing was typed. The message stays until it is dismissed.
    return { note: put(note, id, { ...IDLE, outcome, taskCompleted: true }), done: nextDone };
  }
  // The task is completed but the note is NOT saved: the draft stays, and the panel says so.
  return { note: put(note, id, { ...current, open: true, saving: false, outcome, taskCompleted: true, fieldErrors: NO_ERRORS, needsReload: false }), done: nextDone };
}

interface ThrownLike {
  kind?: unknown;
  status?: unknown;
  code?: unknown;
  message?: unknown;
  details?: unknown;
}

function serverMessage(t: ThrownLike): string | null {
  return typeof t.message === 'string' && t.message.trim() !== '' ? t.message.trim() : null;
}

/**
 * What a failed or refused request means for the task and the note, and which field to point at. Pure. `hasNote` says whether a note was typed (so the
 * message does not claim a note is kept when there is none).
 */
export function describeNoteFailure(thrown: unknown, hasNote: boolean = true): { outcome: NoteOutcome; fieldErrors: NoteFieldErrors; needsReload: boolean } {
  const t: ThrownLike = thrown !== null && typeof thrown === 'object' ? (thrown as ThrownLike) : {};
  const status = typeof t.status === 'number' ? t.status : 0;
  const code = typeof t.code === 'string' ? t.code : null;
  const message = serverMessage(t);
  const field = t.details !== null && typeof t.details === 'object' && typeof (t.details as { field?: unknown }).field === 'string' ? (t.details as { field: string }).field : null;
  const keep = 'Your text is kept. You can try again.';
  const failed = (detail: string | null): NoteOutcome => ({ kind: 'not-completed', tone: 'error', headline: hasNote ? NOT_COMPLETED_KEPT_TEXT : NOT_COMPLETED_TEXT, detail });

  if (t.kind === 'timeout' || t.kind === 'network') {
    return {
      outcome: {
        kind: 'unconfirmed',
        tone: 'error',
        headline: 'We could not confirm the result.',
        detail: 'The task may or may not be completed, and the note may or may not be saved. Your text is kept. Reload the list to check before trying again.',
      },
      fieldErrors: NO_ERRORS,
      needsReload: true,
    };
  }
  if (status === 400 && (code === 'TEXT_REQUIRED' || field === 'note.text')) {
    return { outcome: failed(null), fieldErrors: { text: message ?? 'Add the note text.', activityDate: null }, needsReload: false };
  }
  if (status === 400 && (code === 'INVALID_DATE' || code === 'ACTIVITY_DATE_IN_FUTURE' || field === 'note.activityDate')) {
    return { outcome: failed(null), fieldErrors: { text: null, activityDate: message ?? DATE_INVALID }, needsReload: false };
  }
  if (status === 409 && code === 'STALE_STATE') {
    return {
      outcome: {
        kind: 'changed',
        tone: 'error',
        headline: hasNote ? 'This task changed, so it was not completed here. Your note was not saved.' : 'This task changed, so it was not completed here.',
        detail: `${message ?? 'Reload the list to see its current state.'}${hasNote ? ` ${keep}` : ''}`,
      },
      fieldErrors: NO_ERRORS,
      needsReload: true,
    };
  }
  if (status === 403) return { outcome: failed(message ?? 'You are not allowed to complete this task.'), fieldErrors: NO_ERRORS, needsReload: false };
  if (status === 409) return { outcome: failed(message), fieldErrors: NO_ERRORS, needsReload: false };
  if (status >= 500) return { outcome: failed(message ?? 'The server could not save it. Nothing was changed.'), fieldErrors: NO_ERRORS, needsReload: false };
  return { outcome: failed(message), fieldErrors: NO_ERRORS, needsReload: false };
}

/** The request failed or was refused: the task is still open, the draft is kept, and trying again is allowed. Applies only to a request in progress. */
export function noteFailed(note: DoneNoteState, done: DoneState, id: string, thrown: unknown): { note: DoneNoteState; done: DoneState } {
  const current = itemNote(note, id);
  if (!current.saving) return { note, done };
  const failure = describeNoteFailure(thrown, current.text.trim() !== '');
  return {
    note: put(note, id, { ...current, open: true, saving: false, outcome: failure.outcome, fieldErrors: failure.fieldErrors, taskCompleted: false, needsReload: failure.needsReload }),
    done: doneSettled(done, id),
  };
}

// ───────────────────────── what the panel shows ─────────────────────────

export interface DoneNoteView {
  open: boolean;
  text: string;
  activityDate: string;
  /** The latest date the input accepts (today, Phoenix). */
  maxDate: string;
  complete: { label: typeof COMPLETE_TASK_LABEL | typeof COMPLETING_LABEL; disabled: boolean };
  fieldErrors: NoteFieldErrors;
  outcome: NoteOutcome | null;
  /** True while text the person typed is held and NOT saved (so the panel must not look finished). */
  draftKept: boolean;
  needsReload: boolean;
  /** What the panel may say about the note. `saved` only after the server said `noteSaved: true`. */
  noteStatus: 'none' | 'saved' | 'not-saved';
}

/** `rowStatus` is the status the row shows now (the server's): "Complete task" is available only while the task is open. */
export function describeDoneNote(state: DoneNoteState, id: string, today: string, rowStatus: string): DoneNoteView {
  const item = itemNote(state, id);
  const unsaved = item.outcome !== null && (item.outcome.kind === 'completed-note-unsaved' || item.outcome.kind === 'already-completed-note-unsaved' || item.outcome.kind === 'not-completed' || item.outcome.kind === 'unconfirmed' || item.outcome.kind === 'changed');
  return {
    open: item.open,
    text: item.text,
    activityDate: item.activityDate === '' ? today : item.activityDate,
    maxDate: today,
    complete: { label: item.saving ? COMPLETING_LABEL : COMPLETE_TASK_LABEL, disabled: item.saving || !OPEN_STATUSES.includes(rowStatus) },
    fieldErrors: item.fieldErrors,
    outcome: item.outcome,
    draftKept: item.text.trim() !== '' && (item.open || item.outcome !== null) && unsaved,
    needsReload: item.needsReload,
    noteStatus: item.outcome === null ? 'none' : item.outcome.kind === 'completed-note-saved' ? 'saved' : unsaved ? 'not-saved' : 'none',
  };
}

export interface NoteNotice {
  id: string;
  outcome: NoteOutcome;
  /** The text the person typed, when it is held and NOT saved (so a notice never loses it); null otherwise. */
  draft: string | null;
}

/**
 * The messages of finished or failed completions for a place of their own, because the completed row leaves the Active tab (like the Undo offer): a
 * closed panel's outcome (saved, or no note), and the outcome of a task that is now completed with its note NOT saved (with the kept text). A panel that
 * is open on a task that is still open shows its own outcome beside the draft instead.
 */
export function noteNotices(state: DoneNoteState): NoteNotice[] {
  const out: NoteNotice[] = [];
  for (const id of Object.keys(state.items)) {
    const item = state.items[id];
    if (item.outcome === null || (item.open && !item.taskCompleted)) continue;
    const unsaved = item.outcome.kind === 'completed-note-unsaved' || item.outcome.kind === 'already-completed-note-unsaved';
    out.push({ id, outcome: item.outcome, draft: unsaved && item.text.trim() !== '' ? item.text.trim() : null });
  }
  return out;
}
